import Stack from '@/util/Stack';
import { useSessionStorage } from '@/util/useStorage';
import type { ReactNode } from 'react';
import { createContext, useContext, useRef } from 'react';
import type { DateRange } from './types';
import { isDateRangeEqual } from './utils';

export interface DateRangeStateContextProps {
  dateRange: DateRange;
  setDateRange: (dateRange: DateRange) => void;
  undo: () => void;
  enableUndo: boolean;
}

export const DateRangeStateContext =
  createContext<DateRangeStateContextProps | null>(null);

interface DateRangeStateProviderProps {
  defaultDateRange: DateRange;
  persistKey: string;
  children: ReactNode;
}

const DateRangeStateProvider = ({
  defaultDateRange,
  persistKey,
  children,
}: DateRangeStateProviderProps) => {
  const [dateRange, setDateRange] = useSessionStorage<DateRange>(
    persistKey,
    defaultDateRange,
    {
      deserialize: (data) => ({
        from: new Date(data.from),
        to: new Date(data.to),
      }),
    }
  );
  const stack = useRef(new Stack<DateRange>());

  const handleSetDateRange = (range: DateRange) => {
    stack.current.push(range);
    setDateRange(range);
  };

  const handleUndo = () => {
    stack.current.pop();
    const range = stack.current.peek();
    setDateRange(range ?? defaultDateRange);
  };

  return (
    <DateRangeStateContext.Provider
      value={{
        dateRange,
        setDateRange: handleSetDateRange,
        undo: handleUndo,
        enableUndo:
          stack.current.size !== 0 ||
          !isDateRangeEqual(dateRange, defaultDateRange),
      }}
      children={children}
    />
  );
};

export const useDateRangeState = () => useContext(DateRangeStateContext);

export const useDateRangeStateStrict = () => {
  const ctx = useContext(DateRangeStateContext);

  if (!ctx) {
    throw new Error('No date range context available');
  }

  return ctx;
};

export default DateRangeStateProvider;

interface StaticDateRangeProviderProps {
  dateRange: DateRange;
  children: ReactNode;
}

export const StaticDateRangeProvider = ({
  dateRange,
  children,
}: StaticDateRangeProviderProps) => (
  <DateRangeStateContext.Provider
    value={{
      dateRange,
      setDateRange: () => {},
      undo: () => {},
      enableUndo: false,
    }}
    children={children}
  />
);
